import { useEffect, useState, useMemo } from 'react';
import { Drawer, Space, Tabs } from 'antd';
import { CloseSquareFilled } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { FiFilter } from '@react-icons/all-files/fi/FiFilter';
import { useSearchParams } from '../../utils';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import ContentHeader from '../../components/ContentHeader';
import { PlacesAutoComplete, ZorbaButton } from '../../components/forms';
import {
  SET_TAX_ASSESSOR_DATA,
  SET_INPUT_OBJECT,
} from '../../reducer/Buyer/types';
import { getMatchedBuyers } from '../../reducer/Buyer/actions';
import { createProviderButtons } from './constants';
import {
  BUTTON_VARIANT_LIGHT,
  BUTTON_VARIANT_DARK,
} from '../../components/forms/ZorbaButton/constants';
import MatchingBuyerFilter from './Components/MatchingBuyerFilter';
import { MatchingBuyersTab } from './Components/MatchingBuyersTab';
import { AddressActivityTab } from './Components/AddressActivityTab';

import './index.scss';

const BuyerCheckerPage = () => {
  useDocumentTitle('Buyer Checker');
  const [searchParams] = useSearchParams();
  const queryAddress = searchParams.get('query');
  const [address, setAddress] = useState();
  const [params, setParams] = useState({
    maxAllowableOffer: undefined,
    grossRent: undefined,
    rehabCost: undefined,
  });
  const [buyerFilteredList, setBuyerFilteredList] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const {
    isCheckingBuyers,
    inputObj,
    buyers,
    maxOffer,
    taxAssessorData,
    isGeneratingOffer,
  } = useSelector(({ BuyerReducer }) => BuyerReducer);
  const { zillowData } = inputObj || {};
  const dispatch = useDispatch();
  const onPlacesChange = (newAddress) => {
    if (!newAddress) return;
    setAddress(newAddress);

    dispatch(getMatchedBuyers(newAddress));
  };
  const buyersWithMatchingBuybox = buyers.filter((item) => item.status);

  const actionButtons = useMemo(
    () => createProviderButtons(zillowData?.hdpUrl, inputObj?.hubspotDealId),
    [zillowData?.hdpUrl, inputObj?.hubspotDealId],
  );

  const onFilterHandler = (selectedBuyer = []) => {
    const updatedFilters = { ...selectedFilters, buyers: selectedBuyer };
    setSelectedFilters(updatedFilters);

    if (!selectedBuyer.length) setBuyerFilteredList(buyers);
    else {
      const filteredBuyer = buyers.filter((item) =>
        selectedBuyer.includes(item.buyer),
      );
      setBuyerFilteredList(filteredBuyer);
    }
  };

  useEffect(() => {
    setParams({
      maxAllowableOffer: inputObj?.askingPrice,
      grossRent: inputObj?.zillowData?.rentZestimate,
      bedroom: inputObj?.bedroom,
      bathroom: inputObj?.bathroom,
      yearBuilt: inputObj?.yearBuilt,
      squareFootage: inputObj?.squareFootage,
      lotSize: inputObj?.lotSize,
      schoolRating: inputObj?.zillowData?.schoolRating
        ? inputObj?.zillowData?.schoolRating.toFixed(2)
        : '',
      hasPool: inputObj?.hasPool,
      parkingSpaces: inputObj?.parkingSpaces,
      floodZone: inputObj?.floodZone,
      parkingTypes: inputObj?.parkingTypes || [],
      propertyType: inputObj?.propertyType,
    });
  }, [inputObj]);

  useEffect(() => {
    setBuyerFilteredList(buyers);
  }, [buyers]);

  useEffect(
    () => () => {
      dispatch({
        type: SET_TAX_ASSESSOR_DATA,
        payload: { taxAssessorData: null },
      });
      dispatch({
        type: SET_INPUT_OBJECT,
        payload: { inputObj: null },
      });
    },
    [],
  );

  const items = [
    {
      key: '1',
      label: 'Matching buyers',
      children: (
        <MatchingBuyersTab
          address={address}
          isCheckingBuyers={isCheckingBuyers}
          zillowData={zillowData}
          taxAssessorData={taxAssessorData}
          inputObj={inputObj}
          params={params}
          setParams={setParams}
          buyers={buyers}
          buyerFilteredList={buyerFilteredList}
          buyersWithMatchingBuybox={buyersWithMatchingBuybox}
          isGeneratingOffer={isGeneratingOffer}
          maxOffer={maxOffer}
          selectedFilters={selectedFilters}
        />
      ),
    },
    {
      key: '2',
      label: 'Address activity',
      children: <AddressActivityTab address={address} />,
    },
  ];

  return (
    <>
      <ContentHeader
        title="Address Lookup Tool"
        variant={BUTTON_VARIANT_LIGHT}
        actionButtons={actionButtons}
      />
      <div className="matching-buyer-container">
        <div className="search-container">
          <PlacesAutoComplete
            title={null}
            disabled={isCheckingBuyers}
            allowFullAddress
            placeholder="Start by typing the property address"
            onChange={(value) => onPlacesChange(value)}
            initialInputText={queryAddress}
            clearInputOnSelect={false}
            clearInputOnBlur={false}
            allowClear={{ clearIcon: <CloseSquareFilled /> }}
          />
          {address &&
            buyersWithMatchingBuybox?.length > 0 &&
            !isCheckingBuyers && (
              <Space>
                <ZorbaButton
                  variant={BUTTON_VARIANT_DARK}
                  icon={<FiFilter />}
                  onClick={() => setIsFilterDrawerOpen(true)}
                >
                  Filter
                  {selectedFilters?.buyers?.length > 0
                    ? ` (${selectedFilters?.buyers?.length})`
                    : ''}
                </ZorbaButton>
              </Space>
            )}
        </div>
        {!isCheckingBuyers && (
          <span style={{ color: '#3F434A' }}>
            Type a specific U.S. home address to gain insights such as potential
            buyers for the property, its pricing history, offers from our
            buyers, and a record of all sellers who submitted this deal to us.
          </span>
        )}
        {isCheckingBuyers && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '25px',
            }}
          >
            Fetching data from the tax assessor, recorded documents, Zillow, and
            Zorba’s offers and deals database...
          </div>
        )}
        {address && !isCheckingBuyers && (
          <Tabs defaultActiveKey="1" items={items} />
        )}
      </div>
      <Drawer
        width={420}
        title="Filters"
        placement="right"
        onClose={() => setIsFilterDrawerOpen(false)}
        open={isFilterDrawerOpen}
      >
        <MatchingBuyerFilter
          buyers={buyers}
          setIsFilterDrawerOpen={setIsFilterDrawerOpen}
          onFilter={onFilterHandler}
        />
      </Drawer>
    </>
  );
};

export default BuyerCheckerPage;
